import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { readLocation } from "@/components/LocationPill";
import WeatherPulseNav from "./WeatherPulseNav";
import { MapPin } from "@phosphor-icons/react";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`);

/** Local Weather Journal — the day's community observation timeline for a
 * place. Seed of "The World's Living Weather Almanac". */
export default function WeatherJournal() {
  const [loc, setLoc] = useState(readLocation);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const onChange = () => setLoc(readLocation());
    window.addEventListener("np:location-changed", onChange);
    return () => window.removeEventListener("np:location-changed", onChange);
  }, []);

  useEffect(() => {
    if (!loc?.lat || !date) return;
    let alive = true;
    setLoading(true);
    http.get("/weather-pulse/journal", { params: { lat: loc.lat, lon: loc.lon, date, radius_km: 50 } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData({ entries: [], count: 0 }); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [loc?.lat, loc?.lon, date]);

  const place = [loc?.city, loc?.state, loc?.country].filter(Boolean).join(", ") || "your area";

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="wp-journal-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <ListenTranslateBar text="Your local weather journal — forecasts, almanac and personal observations." testidPrefix="ltbar-wp-journal" />
        <div className="mt-4"><PageGuide pageKey="weather:journal" testidPrefix="wp-journal" /></div>
      </div>
      <div className="max-w-[900px] mx-auto px-5 md:px-10">
        <WeatherPulseNav />
        <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-2">/ The living almanac</div>
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">Weather journal — {place}.</h1>
        <p className="mt-2 text-sm text-[#3A3830] max-w-2xl">
          Every community observation within 50 km, in time order. Day by day, these entries become this place's permanent weather record.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <input
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            data-testid="wp-journal-date"
            className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"
          />
          <div className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)]">
            <MapPin size={12} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> {place}
          </div>
        </div>

        <div className="mt-6" data-testid="wp-journal-entries">
          {loading ? (
            <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div>
          ) : !data || data.entries.length === 0 ? (
            <div className="rounded-2xl border border-black/10 bg-white p-6 text-sm text-[#3A3830]">
              Nothing recorded here on {date} yet — scroll up and report what's outside your window.
            </div>
          ) : (
            <ol className="relative border-l-2 border-black/10 ml-2 space-y-3">
              {data.entries.map((e, i) => (
                <li key={e.id} data-testid={`wp-journal-entry-${i}`} className="ml-4 rounded-xl border border-black/10 bg-white p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono-tech text-[10px] tracking-[0.2em] text-[color:var(--np-brand,#B85F2A)]">
                      {(String(e.observed_at || "").slice(11, 16)) || "—"}
                    </span>
                    <span className="font-medium text-sm">{e.emoji} {e.condition_label}</span>
                    {e.measurements?.temperature_c != null && <span className="text-xs">{e.measurements.temperature_c}°C</span>}
                    {e.measurements?.rainfall_mm != null && <span className="text-xs">{e.measurements.rainfall_mm} mm</span>}
                    <span className="ml-auto font-mono-tech text-[8px] tracking-[0.18em] uppercase text-[color:var(--np-muted,#3A3830)]">
                      {[e.city, e.country].filter(Boolean).join(", ")}
                    </span>
                  </div>
                  {e.comment && <p className="mt-1 text-xs text-[#3A3830]/85">{e.comment}</p>}
                  {e.photo && <img src={abs(e.photo)} alt="" loading="lazy" className="mt-2 w-full max-h-48 object-cover rounded-lg" />}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </main>
  );
}
